/**
 * COLLECTIONS THAT SURVIVE THE WIRE (spec §66C).
 *
 * `JSON.stringify(new Map([["a", 1]]))` is `"{}"`. It does not fail, it does
 * not warn: it silently produces an empty object.
 *
 * That matters more here than it looks. A `PlanModel` is five Maps — nodes,
 * walls, openings, objects, anchors — so a plan sent naively over the shared
 * API arrives as an empty floor, and the receiving editor draws nothing at
 * all while reporting no error whatsoever. The same silence would turn
 * "three pending upstream changes" into "none".
 *
 * So the server encodes them explicitly and this rebuilds them. The shape is
 * deliberately obvious rather than clever, and it round-trips nested
 * structures because a Map of records containing Maps is exactly what a plan
 * is.
 */
export function reviveWire(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(reviveWire);
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (Array.isArray(record.__map)) {
      return new Map((record.__map as Array<[unknown, unknown]>).map(([k, v]) => [k, reviveWire(v)]));
    }
    if (Array.isArray(record.__set)) return new Set(record.__set);
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(record)) out[key] = reviveWire(entry);
    return out;
  }
  return value;
}
